import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Edit, Eye, Building, Users, TrendingUp, Euro, Filter } from 'lucide-react';
import { Subsidiary, Department, Project, User } from '@/entities/all';
import SubsidiaryForm from '../components/subsidiary/SubsidiaryForm';
import SubsidiaryDetail from '../components/subsidiary/SubsidiaryDetail';

// @ts-nocheck
export default function SubsidiaryManagement() {
    const [subsidiaries, setSubsidiaries] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [isLoading, setIsLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [selectedSubsidiary, setSelectedSubsidiary] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [subsidiariesData, departmentsData, projectsData, usersData] = await Promise.all([
                Subsidiary.list('-created_date'),
                Department.list(),
                Project.list(),
                User.list()
            ]);
            setSubsidiaries(subsidiariesData);
            setDepartments(departmentsData);
            setProjects(projectsData);
            setUsers(usersData);
        } catch (error) {
            console.error('Erreur lors du chargement:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getSubsidiaryKPIs = (subsidiary) => {
        const subsidiaryProjects = projects.filter(p => p.subsidiary_id === subsidiary.id);
        const subsidiaryUsers = users.filter(u => u.subsidiary_id === subsidiary.id);
        const subsidiaryDepartments = departments.filter(d => d.subsidiary_number === subsidiary.subsidiary_number);
        const totalBudget = subsidiaryProjects.reduce((sum, p) => sum + (p.total_budget || 0), 0);

        return {
            projectCount: subsidiaryProjects.length,
            userCount: subsidiaryUsers.length,
            departmentCount: subsidiaryDepartments.length,
            totalBudget,
            activeProjects: subsidiaryProjects.filter(p => ['in_progress', 'approved'].includes(p.status)).length
        };
    };

    const filteredSubsidiaries = subsidiaries.filter(subsidiary => {
        const matchesSearch = subsidiary.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            subsidiary.code?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || subsidiary.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return "0€";
        if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)} M€`;
        if (amount >= 1000) return `${(amount / 1000).toFixed(0)} K€`;
        return `${amount.toLocaleString('fr-FR')} €`;
    };

    const handleFormSubmit = async (data) => {
        try {
            if (selectedSubsidiary) {
                await Subsidiary.update(selectedSubsidiary.id, data);
            } else {
                await Subsidiary.create(data);
            }
            setIsFormOpen(false);
            setSelectedSubsidiary(null);
            loadData();
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
        }
    };

    const handleEdit = (subsidiary) => {
        setSelectedSubsidiary(subsidiary);
        setIsFormOpen(true);
    };

    const handleView = (subsidiary) => {
        setSelectedSubsidiary(subsidiary);
        setIsDetailOpen(true);
    };

    const handleNew = () => {
        setSelectedSubsidiary(null);
        setIsFormOpen(true);
    };

    // Calculate global KPIs
    const globalKPIs = {
        totalSubsidiaries: subsidiaries.length,
        activeSubsidiaries: subsidiaries.filter(s => s.status === 'active').length,
        totalProjects: projects.length,
        totalBudget: projects.reduce((sum, p) => sum + (p.total_budget || 0), 0)
    };

    if (isLoading) {
        return (
            <div className="p-8">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 bg-gray-200 rounded w-64"></div>
                    <div className="grid grid-cols-4 gap-4">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="h-24 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                    <div className="h-96 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            {isFormOpen && (
                <SubsidiaryForm
                    isOpen={isFormOpen}
                    onClose={() => {
                        setIsFormOpen(false);
                        setSelectedSubsidiary(null);
                    }}
                    onSubmit={handleFormSubmit}
                    subsidiary={selectedSubsidiary}
                    users={users}
                    departments={departments}
                />
            )}

            {isDetailOpen && selectedSubsidiary && (
                <SubsidiaryDetail
                    isOpen={isDetailOpen}
                    onClose={() => setIsDetailOpen(false)}
                    subsidiary={selectedSubsidiary}
                    departments={departments.filter(d => d.subsidiary_number === selectedSubsidiary.subsidiary_number)}
                    projects={projects.filter(p => p.subsidiary_id === selectedSubsidiary.id)}
                    users={users.filter(u => u.subsidiary_id === selectedSubsidiary.id)}
                    onEdit={handleEdit}
                />
            )}

            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-start mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Gestion des Filiales</h1>
                        <p className="text-gray-600 mt-1">Administration des filiales et structures organisationnelles</p>
                    </div>
                    <Button className="bg-teal-600 hover:bg-teal-700 gap-2" onClick={handleNew}>
                        <Plus className="w-4 h-4" />
                        Nouvelle Filiale
                    </Button>
                </div>

                {/* KPIs Globaux */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <Card className="border-0 shadow-lg">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Filiales Totales</p>
                                    <p className="text-3xl font-bold text-gray-900">{globalKPIs.totalSubsidiaries}</p>
                                    <p className="text-xs text-green-600">{globalKPIs.activeSubsidiaries} actives</p>
                                </div>
                                <div className="p-3 bg-teal-100 rounded-lg">
                                    <Building className="w-6 h-6 text-teal-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Projets CAPEX</p>
                                    <p className="text-3xl font-bold text-gray-900">{globalKPIs.totalProjects}</p>
                                </div>
                                <div className="p-3 bg-blue-100 rounded-lg">
                                    <TrendingUp className="w-6 h-6 text-blue-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Budget Global</p>
                                    <p className="text-3xl font-bold text-gray-900">{formatCurrency(globalKPIs.totalBudget)}</p>
                                </div>
                                <div className="p-3 bg-green-100 rounded-lg">
                                    <Euro className="w-6 h-6 text-green-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Utilisateurs</p>
                                    <p className="text-3xl font-bold text-gray-900">{users.length}</p>
                                </div>
                                <div className="p-3 bg-yellow-100 rounded-lg">
                                    <Users className="w-6 h-6 text-yellow-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Filtres et Actions */}
                <Card className="mb-6 border-0 shadow-lg">
                    <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            <div className="flex-1">
                                <Label className="text-sm font-medium text-gray-700">Recherche</Label>
                                <div className="relative mt-1">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <Input
                                        placeholder="Rechercher par nom ou code..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10"
                                    />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <Label className="text-sm font-medium text-gray-700">Statut</Label>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-40">
                                        <SelectValue placeholder="Tous" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les statuts</SelectItem>
                                        <SelectItem value="active">Actives</SelectItem>
                                        <SelectItem value="inactive">Inactives</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <Button variant="outline" className="gap-2">
                                <Filter className="w-4 h-4" />
                                Plus de filtres
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Liste des Filiales */}
                <Card className="border-0 shadow-lg">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50 hover:bg-gray-50">
                                    <TableHead className="w-[300px]">Filiale</TableHead>
                                    <TableHead>Localisation</TableHead>
                                    <TableHead>Projets</TableHead>
                                    <TableHead>Budget Total</TableHead>
                                    <TableHead>Équipes</TableHead>
                                    <TableHead>Statut</TableHead>
                                    <TableHead className="w-[100px]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredSubsidiaries.map((subsidiary) => {
                                    const kpis = getSubsidiaryKPIs(subsidiary);

                                    return (
                                        <TableRow
                                            key={subsidiary.id}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => handleView(subsidiary)}
                                        >
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="w-12 h-12">
                                                        {subsidiary.logo_url ? (
                                                            <img src={subsidiary.logo_url} alt={subsidiary.name} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <AvatarFallback
                                                                className="text-white font-bold text-sm"
                                                                style={{backgroundColor: subsidiary.color || '#0D9488'}}
                                                            >
                                                                {subsidiary.code?.substring(0, 2) || subsidiary.name?.substring(0, 2)}
                                                            </AvatarFallback>
                                                        )}
                                                    </Avatar>
                                                    <div>
                                                        <p className="font-semibold text-gray-900">{subsidiary.name}</p>
                                                        <p className="text-sm text-gray-500">{subsidiary.code}</p>
                                                        {subsidiary.industry && (
                                                            <p className="text-xs text-gray-400">{subsidiary.industry}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium">{subsidiary.city || '—'}</p>
                                                    <p className="text-sm text-gray-500">{subsidiary.country || '—'}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-lg">{kpis.projectCount}</span>
                                                    <div className="text-xs text-gray-500">
                                                        <p>{kpis.activeProjects} actifs</p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <p className="font-semibold">{formatCurrency(kpis.totalBudget)}</p>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">{kpis.userCount}</span>
                                                    <span className="text-sm text-gray-500">users</span>
                                                    <span className="text-sm text-gray-400">•</span>
                                                    <span className="font-medium">{kpis.departmentCount}</span>
                                                    <span className="text-sm text-gray-500">depts</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={subsidiary.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                                    {subsidiary.status === 'active' ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="h-8 w-8"
                                                        >
                                                            <MoreHorizontal className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleView(subsidiary);
                                                            }}
                                                            className="flex items-center gap-2"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                            Voir le détail
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEdit(subsidiary);
                                                            }}
                                                            className="flex items-center gap-2"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                            Modifier
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {filteredSubsidiaries.length === 0 && (
                            <div className="text-center py-12">
                                <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">Aucune filiale trouvée pour les critères actuels</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
